/**
 * Floating back-nav that appears on every platform-admin page.
 *
 * Gives Super Admins a consistent "← Platform Console" button + a dashboard
 * shortcut, without touching any existing page component. Mounted once in
 * App.js inside the Routes so it renders over platform/admin pages but not
 * on the marketing site, org dashboards, login, etc.
 *
 * Visibility rules:
 *   • Only renders on /platform/* and /admin/* routes
 *   • Hidden on /platform/console itself (that page has its own header)
 *   • Hidden on /login, /request-demo, /og-preview, /admin-panel (legacy redirect)
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Command, LayoutDashboard } from 'lucide-react';

const DISPLAY = "'Space Grotesk', ui-sans-serif, system-ui, sans-serif";

// Human labels for known routes — fallback is a prettified path
const ROUTE_LABELS = {
  '/platform/console': 'Platform Console',
  '/platform/pipeline': 'Pipeline',
  '/platform/admin': 'Platform Admin',
  '/platform/nexus': 'Nexus Network',
  '/platform/analytics': 'Platform Analytics',
  '/platform/employees': 'Employees',
  '/platform/financials': 'Financials',
  '/platform/system-health': 'System Health',
  '/platform/support': 'Support',
  '/platform/leads': 'Platform Leads',
  '/platform/chatbot-analytics': 'Chatbot Analytics',
  '/platform/command-center': 'Neural Command Center',
  '/platform/ai-team-brain': 'Neural Command Center',
  '/platform/guardian-qa': 'Guardian QA',
  '/platform/crisis-command': 'Crisis Command',
  '/platform/nova-knowledge': "Nova's Knowledge Base",
  '/platform/support-command': 'Support Command',
  '/platform/training-effectiveness': 'Training Effectiveness',
  '/admin/email-health': 'Email Health',
  '/admin/demo-requests': 'Demo Requests',
};

const prettify = (path) =>
  path.replace(/^\/(platform|admin)\//, '').replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export default function PlatformNavBar() {
  const { pathname } = useLocation();

  // Only show on platform/admin routes
  const onPlatformRoute = pathname.startsWith('/platform/') || pathname.startsWith('/admin/');
  // Exclude the console itself (has its own header) and legacy redirects
  const excluded = [
    '/platform/console',
    '/admin-panel',
    '/login',
    '/request-demo',
    '/og-preview',
  ].includes(pathname);

  if (!onPlatformRoute || excluded) return null;

  const currentLabel = ROUTE_LABELS[pathname] || prettify(pathname);
  const isOnPlatformAdmin = pathname === '/platform/admin';

  return (
    <div
      className="fixed top-4 left-1/2 -translate-x-1/2 z-40 pointer-events-none"
      data-testid="platform-nav-bar"
    >
      <div
        className="pointer-events-auto flex items-center gap-0.5 bg-slate-950/90 backdrop-blur-xl border border-violet-500/30 rounded-full pl-1.5 pr-1.5 py-1 shadow-[0_12px_40px_-12px_rgba(109,40,217,0.6)]"
      >
        <Link
          to="/platform/console"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-br from-violet-600 to-violet-800 text-white hover:from-violet-500 hover:to-violet-700 text-[11.5px] transition-all"
          style={{ fontFamily: DISPLAY, fontWeight: 600 }}
          data-testid="platform-nav-console-link"
        >
          <Command className="w-3 h-3" />
          Console
        </Link>

        {!isOnPlatformAdmin && (
          <Link
            to="/platform/admin"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-cyan-300 hover:text-cyan-100 hover:bg-white/5 text-[11.5px] transition-all"
            style={{ fontFamily: DISPLAY, fontWeight: 500 }}
            data-testid="platform-nav-admin-link"
          >
            <LayoutDashboard className="w-3 h-3" />
            Admin
          </Link>
        )}

        {/* Current page label — non-interactive, provides context */}
        <span
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] text-slate-400 border-l border-violet-500/20 ml-1"
          style={{ fontFamily: DISPLAY, fontWeight: 500 }}
          data-testid="platform-nav-current-label"
        >
          {currentLabel}
        </span>
      </div>
    </div>
  );
}
